import { useState } from 'react';

import { DownArrow, UpArrow } from 'ui/icons/navigation';
import { RUMHelpers } from 'services/observability/datadog';

import { DonutChart } from '../charts/DonutChart';
import { EdgedDivider, StyledSlideOverGraphFeatureText } from '../Styled';
import {
  SEE_LESS_EXPAND_BUTTON_TEXT,
  SEE_MORE_EXPAND_BUTTON_TEXT,
  TEST_IDS,
  XAI_DONUT_CHART_HEADER_TEXT,
} from '../../constants';
import { createActionName } from '../../observabilityUtils';
import { getDonutChartData } from '../charts/utils/dataHelpers';
import {
  DisplayTopEventsOrProperties,
  DonutChartContainer,
  DonutChartHeaderText,
  EventOrPropertyListItemContainer,
  EventOrPropertyListItemPercentage,
  XaiDonutColoredDots,
  XaiExpandTextButton,
} from '../charts/Styled';
import { Feature } from '../../types';
import { XaiMonospaceTextTooltipWrapper } from './XaiMonospaceTextTooltipWrapper';

type XaiSlideOverDonutChartContentTypes = {
  features: Array<Feature>;
};

export const XaiSlideOverDonutChartContainer = ({
  features,
}: XaiSlideOverDonutChartContentTypes) => {
  const [buttonExpand, setButtonExpand] = useState(false);
  const formattedFeatures = getDonutChartData(
    features,
    'name',
    'strength',
    true,
    0.02,
    'valueCutoff',
  );
  const chartData = formattedFeatures.filter((feature) => feature.name !== 'Other');
  const chartTitle = chartData.length.toString();
  const expandingListThreshold = formattedFeatures.length;
  const minimumNumberOfFeatures = 3;
  const defaultFeaturesShowing: number = Math.min(minimumNumberOfFeatures, features.length);
  const expandedFeaturesShowing: number = expandingListThreshold;
  const topFeaturesShowing: number = buttonExpand
    ? expandedFeaturesShowing
    : defaultFeaturesShowing;
  return (
    <DonutChartContainer data-test={`${TEST_IDS.DonutChartContainer}`}>
      <DonutChartHeaderText>{XAI_DONUT_CHART_HEADER_TEXT}</DonutChartHeaderText>
      <DonutChart chartTitle={chartTitle} chartData={chartData} />
      <EdgedDivider />
      <DisplayTopEventsOrProperties data-test={`${TEST_IDS.DisplayTopEventsOrProperties}`}>
        {formattedFeatures.slice(0, topFeaturesShowing).map((feature) => {
          const featurePercentage = (feature.y * 100).toFixed(0);
          return (
            <EventOrPropertyListItemContainer key={feature.name}>
              <XaiDonutColoredDots color={feature.color} />
              <XaiMonospaceTextTooltipWrapper name={feature.name} $textMaxWidth={'260'}>
                <StyledSlideOverGraphFeatureText>{feature.name}</StyledSlideOverGraphFeatureText>
              </XaiMonospaceTextTooltipWrapper>
              <EventOrPropertyListItemPercentage>
                {featurePercentage}%
              </EventOrPropertyListItemPercentage>
            </EventOrPropertyListItemContainer>
          );
        })}
        {formattedFeatures.length > minimumNumberOfFeatures && (
          <XaiExpandTextButton
            data-test={`${TEST_IDS.ExpandTextButton}`}
            onClick={() => {
              RUMHelpers.addRUMAction(createActionName('Toggle XAI events data'));
              setButtonExpand((prevState) => !prevState);
            }}
          >
            {buttonExpand ? SEE_LESS_EXPAND_BUTTON_TEXT : SEE_MORE_EXPAND_BUTTON_TEXT}
            {buttonExpand ? <UpArrow /> : <DownArrow />}
          </XaiExpandTextButton>
        )}
      </DisplayTopEventsOrProperties>
    </DonutChartContainer>
  );
};
